import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PackageOpen, RefreshCw, Trash2 } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import api from '../services/api';
import { useToast } from './shared/Toast';
import ConfirmModal from './shared/ConfirmModal';
import { formatMoney } from '../utils/formatMoney';
import { LoadFailed } from './settings/instantSettings';
import { fmtDM, fmtLongDate, fmtNum, fmtTime, toAthensYmd } from './home/format';
import { completeRange, periodText } from './StoreOrdersPanel';
import {
  Column, DANGER_GHOST_BTN, DataTable, DateRange, DateRangeValue, DetailPanel, EmptyState, GHOST_BTN, KeyValue,
  KeyValueItem, ListPage, ListToolbar, Stat, Stats, presetRange,
} from './shared/listPage';

/**
 * Διαχειριστικό καταστήματος → «Φύρα» — διάταξη «Πίνακας» (14/10/2026), ίδια με τις «Παραγγελίες»: η
 * περίοδος στην κεφαλίδα (προεπιλογή σήμερα, ημέρες Ελλάδας), τα σύνολα σε μία γραμμή κειμένου (όχι
 * κουτιά), οι καταχωρήσεις ή — με την «Προβολή» — τα σύνολα ανά προϊόν, και συρτάρι με την καταχώρηση και
 * τη «Διαγραφή» για τα λάθη. Η καταχώρηση γίνεται από το ταμείο (shared/WasteModal) — εδώ είναι ο έλεγχος.
 *
 * Ίδια κλήσεις με πριν: GET /api/waste/:storeId?from&to (και τα δύο πάντα: χωρίς αυτά ο server γυρίζει την
 * τρέχουσα περίοδο ταμείου, γι' αυτό δεν υπάρχει «Όλες»), DELETE /api/waste/:storeId/:id. Η διαγραφή είναι
 * οριστική: ως 14/10 έφευγε με ένα κλικ στο εικονίδιο της γραμμής, τώρα από το συρτάρι με επιβεβαίωση.
 */

interface WasteEntry {
  id: number;
  employee_id?: number | null;
  employee_name: string | null;
  product_name: string;
  quantity: number;
  unit_price: number;
  total_value: number;
  reason: string | null;
  notes: string | null;
  created_at: string;
}

interface ByProduct { product_name: string; qty: number; value: number }

interface WasteSummary { entries: number; qty: number; value: number }

type LoadState = { kind: 'ready' } | { kind: 'error' | 'forbidden'; message: string | null };
type View = 'entries' | 'products';

const EMPTY_SUMMARY: WasteSummary = { entries: 0, qty: 0, value: 0 };
const ENTRIES_CAP = 500;

const QTY = new Intl.NumberFormat('el-GR', { maximumFractionDigits: 3 });
const fmtQty = (n: number | string | null | undefined): string => QTY.format(Number(n) || 0);

/** «14/10 · 12:34» σε ώρα Ελλάδας, 24ωρη. */
const whenOf = (iso: string | null | undefined): string => {
  const ymd = toAthensYmd(iso);
  const time = fmtTime(iso);
  return ymd && time ? `${fmtDM(ymd)} · ${time}` : '—';
};

const StoreWastePanel: React.FC = () => {
  const { t, i18n } = useTranslation(['storeWaste', 'settings', 'common', 'listPage']);
  const lang = i18n?.language;
  const { store } = useAuth();
  const storeId = store?.id;
  const { showToast } = useToast();
  const [range, setRange] = useState<DateRangeValue>(() => presetRange('today'));
  const [entries, setEntries] = useState<WasteEntry[]>([]);
  const [byProduct, setByProduct] = useState<ByProduct[]>([]);
  const [summary, setSummary] = useState<WasteSummary>(EMPTY_SUMMARY);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [loadState, setLoadState] = useState<LoadState>({ kind: 'ready' });
  const [view, setView] = useState<View>('entries');
  const [selected, setSelected] = useState<WasteEntry | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<WasteEntry | null>(null);
  const [deleting, setDeleting] = useState(false);
  const deletingRef = useRef(false);
  const seq = useRef(0);

  const { from, to } = range;
  const load = useCallback(async () => {
    if (!storeId) return;
    const id = ++seq.current;
    setLoading(true);
    try {
      const res = await api.get(`/waste/${storeId}`, { params: { from, to } });
      if (id !== seq.current) return;
      setEntries(res.data.entries || []);
      setByProduct(res.data.by_product || []);
      setSummary(res.data.summary || EMPTY_SUMMARY);
      setLoadState({ kind: 'ready' });
    } catch (err: any) {
      if (id !== seq.current) return;
      // Στη θέση του πίνακα (ως 14/10 ήταν toast και έμενε η προηγούμενη λίστα — λάθος περίοδος χωρίς ένδειξη).
      setLoadState({
        kind: err?.response?.status === 403 ? 'forbidden' : 'error',
        message: err?.response?.data?.message || null,
      });
    } finally {
      if (id === seq.current) {
        setLoading(false);
        setLoaded(true);
      }
    }
  }, [storeId, from, to]);

  useEffect(() => { load(); }, [load]);

  const reasonText = useCallback((reason: string | null): string | null => (
    reason ? t(`storeWaste:reasons.${reason}`, { defaultValue: reason }) : null
  ), [t]);

  const deleteEntry = async (entry: WasteEntry) => {
    if (!storeId || deletingRef.current) return;
    deletingRef.current = true;
    setDeleting(true);
    try {
      await api.delete(`/waste/${storeId}/${entry.id}`);
      showToast('success', t('storeWaste:delete.done'));
      setSelected(null);
      load();
    } catch (err: any) {
      showToast('error', err.response?.data?.message || t('storeWaste:delete.failed'));
    } finally {
      deletingRef.current = false;
      setDeleting(false);
    }
  };

  const entryColumns = useMemo<Column<WasteEntry>[]>(() => [
    {
      key: 'created_at',
      header: t('storeWaste:columns.time'),
      width: '116px',
      render: e => <span className="tabular-nums text-t2 whitespace-nowrap">{whenOf(e.created_at)}</span>,
      stackRender: e => whenOf(e.created_at),
    },
    {
      key: 'product_name',
      header: t('storeWaste:columns.product'),
      width: 'minmax(140px, 2fr)',
      stack: 'title',
      render: e => <span className="font-medium">{e.product_name}</span>,
      stackRender: e => e.product_name,
    },
    {
      key: 'quantity',
      header: t('storeWaste:columns.qty'),
      width: '80px',
      align: 'right',
      render: e => <span className="text-t2">{fmtQty(e.quantity)}</span>,
      stackRender: e => `× ${fmtQty(e.quantity)}`,
    },
    {
      key: 'total_value',
      header: t('storeWaste:columns.value'),
      width: '104px',
      align: 'right',
      stack: 'end',
      render: e => <span className="font-medium">{formatMoney(e.total_value)}</span>,
      stackRender: e => formatMoney(e.total_value),
    },
    {
      key: 'reason',
      header: t('storeWaste:columns.reason'),
      width: '148px',
      render: e => {
        const label = reasonText(e.reason);
        return label ? <span className="text-t2">{label}</span> : <span className="text-t4">—</span>;
      },
      stackRender: e => reasonText(e.reason) || '',
    },
    {
      key: 'employee_name',
      header: t('storeWaste:columns.employee'),
      width: 'minmax(0, 1fr)',
      render: e => (e.employee_name ? <span className="text-t2">{e.employee_name}</span> : <span className="text-t4">—</span>),
      stackRender: e => e.employee_name || '',
    },
  ], [t, reasonText]);

  const productColumns = useMemo<Column<ByProduct>[]>(() => [
    {
      key: 'product_name',
      header: t('storeWaste:columns.product'),
      width: 'minmax(160px, 3fr)',
      render: p => <span className="font-medium">{p.product_name}</span>,
    },
    {
      key: 'qty',
      header: t('storeWaste:columns.pieces'),
      width: '120px',
      align: 'right',
      render: p => <span className="text-t2">{fmtQty(p.qty)}</span>,
      stackRender: p => t('storeWaste:pieces', { qty: fmtQty(p.qty) }),
    },
    {
      key: 'value',
      header: t('storeWaste:columns.value'),
      width: '120px',
      align: 'right',
      stack: 'end',
      render: p => <span className="font-medium">{formatMoney(p.value)}</span>,
      stackRender: p => formatMoney(p.value),
    },
  ], [t]);

  const detailItems = (e: WasteEntry): KeyValueItem[] => {
    const ymd = toAthensYmd(e.created_at);
    const time = fmtTime(e.created_at);
    return [
      { id: 'quantity', label: t('storeWaste:detail.quantity'), value: fmtQty(e.quantity) },
      { id: 'unitPrice', label: t('storeWaste:detail.unitPrice'), value: formatMoney(e.unit_price) },
      { id: 'value', label: t('storeWaste:detail.value'), value: formatMoney(e.total_value), hint: t('storeWaste:detail.valueHint') },
      { id: 'reason', label: t('storeWaste:detail.reason'), value: reasonText(e.reason) },
      { id: 'employee', label: t('storeWaste:detail.employee'), value: e.employee_name },
      { id: 'recorded', label: t('storeWaste:detail.recorded'), value: ymd && time ? `${fmtLongDate(ymd, lang)} · ${time}` : null },
      ...(e.notes ? [{ id: 'notes', label: t('storeWaste:detail.notes'), value: <span className="whitespace-pre-line">{e.notes}</span> }] : []),
    ];
  };

  // Το Esc (και το X/φόντο) του συρταριού κλείνει ΠΡΩΤΑ το παράθυρο επιβεβαίωσης.
  const closePanel = () => {
    if (confirmDelete) setConfirmDelete(null);
    else setSelected(null);
  };

  const firstLoad = !loaded && loading;
  const empty = (
    <EmptyState icon={PackageOpen} title={t('storeWaste:empty.title')} hint={t('storeWaste:empty.hint')} />
  );

  return (
    <ListPage
      eyebrow={t('settings:nav.groups.register')}
      title={t('settings:nav.waste')}
      subtitle={t('storeWaste:subtitle', { period: periodText(from, to, lang) })}
      actions={<DateRange from={from} to={to} allowAll={false} onChange={r => setRange(completeRange(r))} />}
      testId="store-waste"
    >
      <ListToolbar
        filters={(
          <div className="lp-seg" role="radiogroup" aria-label={t('storeWaste:view.label')} data-testid="waste-view">
            {(['entries', 'products'] as const).map(v => (
              <button key={v} type="button" role="radio" aria-checked={view === v} onClick={() => setView(v)} data-testid={`waste-view-${v}`}>
                {t(`storeWaste:view.${v}`)}
              </button>
            ))}
          </div>
        )}
        actions={(
          <button type="button" className={GHOST_BTN} onClick={() => load()} disabled={loading}>
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} aria-hidden="true" />
            {t('common:refresh')}
          </button>
        )}
      />

      {loadState.kind === 'ready' && !firstLoad && (
        <div className="mb-3" role="status" data-testid="waste-stats">
          <Stats>
            <Stat label={t('storeWaste:stats.entries')} value={fmtNum(summary.entries)} />
            <Stat label={t('storeWaste:stats.qty')} value={fmtQty(summary.qty)} />
            <Stat label={t('storeWaste:stats.value')} value={formatMoney(summary.value)} hint={t('storeWaste:stats.valueHint')} />
            {entries.length >= ENTRIES_CAP && <span className="lp-stat-hint">{t('storeWaste:stats.capped')}</span>}
          </Stats>
        </div>
      )}

      {loadState.kind !== 'ready' ? (
        <LoadFailed
          message={loadState.message || (loadState.kind === 'forbidden' ? t('storeWaste:forbidden') : t('storeWaste:loadFailed'))}
          onRetry={() => load()}
        />
      ) : view === 'entries' ? (
        <DataTable
          columns={entryColumns}
          rows={entries}
          rowKey={e => e.id}
          onRowClick={setSelected}
          selectedKey={selected?.id ?? null}
          loading={loading}
          stickyHeader
          rowLabel={e => t('storeWaste:rowLabel', { product: e.product_name, qty: fmtQty(e.quantity), when: whenOf(e.created_at) })}
          empty={empty}
          testId="waste-table"
        />
      ) : (
        <DataTable
          columns={productColumns}
          rows={byProduct}
          rowKey={p => p.product_name}
          loading={loading}
          stickyHeader
          ariaLabel={t('storeWaste:view.products')}
          empty={empty}
          testId="waste-products"
        />
      )}

      <DetailPanel
        open={selected !== null}
        onClose={closePanel}
        title={selected?.product_name ?? ''}
        subtitle={selected ? [whenOf(selected.created_at), selected.employee_name].filter(Boolean).join(' · ') : undefined}
        footerStart={selected ? (
          <button type="button" className={DANGER_GHOST_BTN} disabled={deleting} onClick={() => setConfirmDelete(selected)}>
            <Trash2 className="w-4 h-4" aria-hidden="true" />
            {t('storeWaste:delete.button')}
          </button>
        ) : undefined}
        footer={<button type="button" className={GHOST_BTN} onClick={() => setSelected(null)}>{t('listPage:close')}</button>}
        testId="waste-detail"
      >
        {selected && <KeyValue items={detailItems(selected)} testId="waste-detail-values" />}
      </DetailPanel>

      <ConfirmModal
        isOpen={confirmDelete !== null}
        title={t('storeWaste:delete.title')}
        message={confirmDelete ? t('storeWaste:delete.message', { product: confirmDelete.product_name, qty: fmtQty(confirmDelete.quantity) }) : ''}
        confirmLabel={t('storeWaste:delete.confirm')}
        cancelLabel={t('storeWaste:delete.cancel')}
        variant="danger"
        onConfirm={() => {
          const entry = confirmDelete;
          setConfirmDelete(null);
          if (entry) deleteEntry(entry);
        }}
        onCancel={() => setConfirmDelete(null)}
      />
    </ListPage>
  );
};

export default StoreWastePanel;
